// What a refused correction means for the editor, keyed by the server's
// error code. One table, one reading of an answer.

/** Why a correction was refused: one value per entry of the table below,
 * plus the two ways no table entry applies: no answer arrived (`network`)
 * or the answer is not one the table knows (`other`). */
export type CorrectionRefusalReason =
  | "in_progress"
  | "phone_invalid"
  | "email_invalid"
  | "zip_mismatch"
  | "rate_limited"
  | "not_allowed"
  | "expired"
  | "network"
  | "other";

export type CorrectionFailure =
  /** The editor stays open with the visitor's values and says `message`. */
  | { kind: "stay"; message: string; reason: CorrectionRefusalReason }
  /** The destination can no longer be changed here. */
  | { kind: "closed"; reason: CorrectionRefusalReason };

export const CORRECTION_CLOSED_MESSAGE =
  "We can't change that here. Give us a call and we'll fix it.";

const TRY_AGAIN_MESSAGE = "That didn't go through. Try again in a moment.";

const tryAgain = (reason: CorrectionRefusalReason): CorrectionFailure => ({
  kind: "stay",
  message: TRY_AGAIN_MESSAGE,
  reason,
});

const PHONE_REJECTED: CorrectionFailure = {
  kind: "stay",
  message: "That phone number doesn't look right.",
  reason: "phone_invalid",
};

const closed = (reason: CorrectionRefusalReason): CorrectionFailure => ({
  kind: "closed",
  reason,
});

const FAILURE_BY_CODE: ReadonlyMap<string, CorrectionFailure> = new Map<
  string,
  CorrectionFailure
>([
  ["submission_in_progress", tryAgain("in_progress")],
  ["phone_invalid", PHONE_REJECTED],
  ["phone_unsupported_region", PHONE_REJECTED],
  [
    "validation_error",
    {
      kind: "stay",
      message: "That email doesn't look right.",
      reason: "email_invalid",
    },
  ],
  [
    "zip_mismatch",
    {
      kind: "stay",
      message: "That ZIP code doesn't match your form.",
      reason: "zip_mismatch",
    },
  ],
  [
    "too_many_attempts",
    {
      kind: "stay",
      message: "Too many tries. Wait a few minutes and try again.",
      reason: "rate_limited",
    },
  ],
  ["correction_not_allowed", closed("not_allowed")],
  ["submission_expired", closed("expired")],
]);

/** The failure a refused correction is: by its error code when the table
 * knows it, else a 404 closes the editor, no answer at all is a try-again for
 * `network`, and any other answer (a server error) a try-again for `other`. */
export function correctionFailureOf(
  statusCode: number | null,
  errorCode: string | null,
): CorrectionFailure {
  const known = errorCode === null ? undefined : FAILURE_BY_CODE.get(errorCode);
  if (known !== undefined) return known;
  if (statusCode === 404) return closed("not_allowed");
  return tryAgain(statusCode === null ? "network" : "other");
}
